import { Ability } from "@/interfaces/ability";
import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import axios from "@/lib/axios";
import useAbilities from "@/modules/roles/composables/useAbilities";
import { useUserAbilitiesStore } from "@/stores/userAbilities";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import { watch } from "vue";
import { ToastInterface, useToast } from "vue-toastification";

interface Props {
  params: Params;
  immediate?: boolean;
}

interface Params {
  userId: string | number;
}

interface CreateParams {
  userId: string | number;
  abilities: number[];
  toast: ToastInterface;
}

const getUserAbilities = async (userId: string) => {
  const { data } = await axios.get<Ability[]>(
    `api/internal/users/${userId}/abilities`,
  );

  return data;
};

const postUserAbility = async (params: CreateParams) => {
  try {
    await axios.post(
      `api/users/${params.userId}/abilities`,
      { abilities: params.abilities },
      { errorHandling: false } as CustomAxiosRequestConfig,
    );

    params.toast.success("Permisos actualizados correctamente");
  } catch (error: any) {
    params.toast.error(error?.response?.data?.message ?? "No tienes permiso para otorgar estos permisos");
  }
};

const useUserInternalAbilities = (props: Props) => {
  const { abilities } = useAbilities();
  const { immediate, params } = props;
  const { setUserAbilities } = useUserAbilitiesStore();
  const { userAbilities } = storeToRefs(useUserAbilitiesStore());
  const toast = useToast();

  const { data, isLoading } = useQuery(
    ["userAbilities", params.userId],
    () => getUserAbilities(params.userId.toString()),
    {
      enabled: immediate,
    },
  );

  const postUserAbilityMutation = useMutation((params: CreateParams) =>
    postUserAbility({
      abilities: params.abilities,
      userId: params.userId,
      toast,
    }),
  );

  watch(
    data,
    (value) => {
      if (value) {
        setUserAbilities(
          abilities.value.map((ability) => {
            const userAbility = value.find(
              (userAbility) => userAbility.id === ability.id,
            );

            return {
              ...ability,
              checked: !!userAbility,
            };
          }),
        );
      }
    },
    {
      immediate,
    },
  );

  return {
    userAbilities,
    isLoading,

    postUserAbility: postUserAbilityMutation.mutate,
    postUserAbilityAsync: postUserAbilityMutation.mutateAsync,
    postUserAbilityLoading: postUserAbilityMutation.isLoading,
  };
};

export default useUserInternalAbilities;
